import { Schema } from "effect";
import {
  AccountId,
  ConversationId,
  ConversationThreadId,
  MessageId,
} from "../../../ids/definitions";
import {
  GmailChannelData,
  InternalNoteChannelData,
  MetaCommentChannelData,
  OutlookChannelData,
  RedoChatChannelData,
  SmsChannelData,
  VoiceChannelData,
} from "./message/channel-data";

export const MESSAGE_CHANNELS = [
  "gmail",
  "outlook",
  "redoChat",
  "facebook",
  "facebookComments",
  "instagram",
  "instagramComments",
  "sms",
  "voice",
  "attentive",
  "postscript",
  "tiktok",
  "contactForm",
  "internalNote",
  "mobileMessage",
] as const;

export const MessageChannel = Schema.Literal(...MESSAGE_CHANNELS);
export type MessageChannel = typeof MessageChannel.Type;

export const MessageOrigin = Schema.Literal("external", "internal");
export type MessageOrigin = typeof MessageOrigin.Type;

export const MessageSenderType = Schema.Literal(
  "merchant",
  "customer",
  "system",
);
export type MessageSenderType = typeof MessageSenderType.Type;

// Base fields shared by all message variants
const BaseMessageFields = {
  accountId: AccountId,
  content: Schema.NullOr(Schema.String),
  conversationThreadId: ConversationThreadId,
  id: MessageId,
  inReplyToId: Schema.NullOr(MessageId),
  inReplyToExternalId: Schema.NullOr(Schema.String),
  origin: MessageOrigin,
  sentAt: Schema.Date,
};

// Channel-specific message variants
const GmailMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("gmail"),
  channelData: Schema.NullOr(GmailChannelData),
});

const OutlookMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("outlook"),
  channelData: Schema.NullOr(OutlookChannelData),
});

const SmsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("sms"),
  channelData: Schema.NullOr(SmsChannelData),
});

const RedoChatMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("redoChat"),
  channelData: Schema.NullOr(RedoChatChannelData),
});

const VoiceMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("voice"),
  channelData: Schema.NullOr(VoiceChannelData),
});

// Meta platform channels share MetaCommentChannelData
const FacebookMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("facebook"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

const FacebookCommentsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("facebookComments"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

const InstagramMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("instagram"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

const InstagramCommentsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("instagramComments"),
  channelData: Schema.NullOr(MetaCommentChannelData),
});

// Channels without specific channelData (always null)
const AttentiveMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("attentive"),
  channelData: Schema.Null,
});

const PostscriptMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("postscript"),
  channelData: Schema.Null,
});

const TiktokMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("tiktok"),
  channelData: Schema.Null,
});

const ContactFormMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("contactForm"),
  channelData: Schema.Null,
});

const InternalNoteMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("internalNote"),
  channelData: Schema.NullOr(InternalNoteChannelData),
});

// Discriminated union on channel
export const Message = Schema.Union(
  GmailMessage,
  OutlookMessage,
  SmsMessage,
  RedoChatMessage,
  VoiceMessage,
  FacebookMessage,
  FacebookCommentsMessage,
  InstagramMessage,
  InstagramCommentsMessage,
  AttentiveMessage,
  PostscriptMessage,
  TiktokMessage,
  ContactFormMessage,
  InternalNoteMessage,
);
export type Message = typeof Message.Type;

export const MessageCursor = Schema.Struct({
  sentAt: Schema.DateFromString,
  id: MessageId,
});
export type MessageCursor = typeof MessageCursor.Type;

export type MessageWithConversationId = Message & {
  conversationId?: ConversationId;
};
